/**
 * Workspace Settings — slide-in panel with Members, Presence, MCP, and Danger Zone.
 *
 * Sub-components:
 * - DeleteWorkspaceModal (type-to-confirm tombstone)
 * - LeaveWorkspaceModal  (lightweight leave confirmation)
 * - RemoveWriterModal    (in-app confirmation for writer removal — replaces raw confirm())
 * - WorkspaceDeletedOverlay (tombstone received from sync)
 * - PresenceIndicator   (TopBar compact avatar chip)
 */

import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import {
    X, Settings, Users, Eye, Trash2, LogOut, AlertTriangle, Bell, BellOff,
    GitBranch, KeyRound, RefreshCw,
} from 'lucide-react'
import { useWorkspaceMetadata, useEngineManager, useWorkspace, useSync, useRegistry } from '../hooks/useWorkspace'
import { useMcpConnections } from '../hooks/useMcpConnections'
import { useToast } from './Toast'
import type { WriterInfo, PresenceEntry, Workspace } from '../engine/types'
import type { McpConfig, McpConnectionEntry, McpWorkspaceConfig } from '../engine/mcp/types'
import ServerJobsPanel from './ServerJobsPanel'
import {
    getNotificationState, subscribeToPush, unsubscribeFromPush,
    unsubscribeBrowserPush, type NotificationState, type NotificationConfigDocument,
    formatNotificationConfig, parseNotificationConfig, provisionNotificationConfig,
} from '../engine/pushSubscription'
import { createWorkspaceAuthProvider } from '@secureblob/sdk/workspaceAuth'
import { decodeWorkspaceSecretV2 } from '@secureblob/sdk/workspaceSecretV2'
import { deriveMcpSeedIdentity } from '../engine/mcp/credentialIdentity'
import { API_BASE_URL } from '../config'
import type { WorkspaceSyncPolicy } from '../engine/types'
import {
    getLocalWorkspaceSyncPolicy,
    setLocalWorkspaceSyncPolicy,
} from '../engine/workspaceSyncPolicy'
import './WorkspaceSettings.css'

// ── Helpers ────────────────────────────────────────────────────

const NOTIFICATION_CONFIG_PATH = '/notification.json'

const WORKSPACE_SYNC_POLICY_OPTIONS: Array<{
    policy: WorkspaceSyncPolicy
    label: string
    badge?: string
    caption: string
    title: string
}> = [
    {
        policy: 'on-demand',
        label: 'On-demand sync',
        badge: 'Default',
        caption: 'Tree stays current; content syncs when opened.',
        title: 'Keep the file tree current and download content when opened',
    },
    {
        policy: 'auto',
        label: 'Auto-sync',
        caption: 'Make files and known SQLite lanes available offline.',
        title: 'Try to make all workspace content available offline',
    },
    {
        policy: 'paused',
        label: 'Pause sync',
        caption: 'Stop background sync on this device.',
        title: 'Pause background sync for this workspace on this device',
    },
]

function truncateKid(kid: string): string {
    return kid.length > 8 ? kid.slice(0, 4) + '…' + kid.slice(-4) : kid
}

function shortAccessKid(kid: string): string {
    return kid.length > 14 ? `${kid.slice(0, 14)}…` : kid
}

function isSameNotificationConfig(
    a: NotificationConfigDocument | null,
    b: NotificationConfigDocument,
): boolean {
    return a?.blobId === b.blobId
        && a.notifyUrl === b.notifyUrl
        && a.notifyToken === b.notifyToken
        && a.defaultAudience === b.defaultAudience
}

type WorkspaceMcpLinkSource = 'registry' | 'linked-ref' | 'live-config' | 'registry-live'

interface WorkspaceMcpLink {
    configBlobId: string
    label: string
    purpose?: string
    source: WorkspaceMcpLinkSource
    access: 'read' | 'read/write' | 'configured'
    hasRegistryReader: boolean
    hasRegistryWriter: boolean
    readerSeedForKid?: string
    writerSeedForKid?: string
}

interface WorkspaceMcpCredentialKids {
    readerKid?: string
    writerKid?: string
}

function workspaceMatchesRefs(workspace: McpWorkspaceConfig, refs: Set<string>): boolean {
    return refs.has(workspace.blobId)
        || Boolean(workspace.alias && refs.has(workspace.alias))
        || refs.has(workspace.title)
}

function findLiveWorkspace(config: McpConfig | undefined, refs: Set<string>): McpWorkspaceConfig | undefined {
    return config?.workspaces.find(workspace => workspaceMatchesRefs(workspace, refs))
}

function resolveWorkspaceMcpLink(
    connection: McpConnectionEntry,
    liveConfig: McpConfig | undefined,
    refs: Set<string>,
    blobId: string,
): WorkspaceMcpLink | null {
    const directRegistryMatch = connection.workspaceBlobIds.includes(blobId)
    const linkedRefMatch = (connection.linkedWorkspaceRefs ?? []).some(ref => refs.has(ref))
    const liveWorkspace = findLiveWorkspace(liveConfig, refs)

    if (!directRegistryMatch && !linkedRefMatch && !liveWorkspace) return null

    const liveSecret = liveWorkspace ? decodeWorkspaceSecretV2(liveWorkspace.workspaceSecret) : null
    const registryReaderSeed = connection.mcpReaderSeeds?.[blobId]
    const registryWriterSeed = connection.mcpWriterSeeds?.[blobId]
    const writerAvailable = Boolean(registryWriterSeed || liveSecret?.writerSeed)
    const readerAvailable = Boolean(registryReaderSeed || liveSecret?.readerSeed)

    const source: WorkspaceMcpLinkSource = directRegistryMatch && liveWorkspace
        ? 'registry-live'
        : liveWorkspace
            ? 'live-config'
            : directRegistryMatch
                ? 'registry'
                : 'linked-ref'

    return {
        configBlobId: connection.configBlobId,
        label: connection.label,
        purpose: liveConfig?.links?.purpose ?? connection.purpose,
        source,
        access: writerAvailable ? 'read/write' : readerAvailable ? 'read' : 'configured',
        hasRegistryReader: Boolean(registryReaderSeed),
        hasRegistryWriter: Boolean(registryWriterSeed),
        readerSeedForKid: registryReaderSeed ?? liveSecret?.readerSeed,
        writerSeedForKid: registryWriterSeed ?? liveSecret?.writerSeed,
    }
}

function formatMcpLinkSource(source: WorkspaceMcpLinkSource): string {
    switch (source) {
        case 'registry-live': return 'registry + live'
        case 'live-config': return 'live config'
        case 'linked-ref': return 'linked ref'
        default: return 'registry'
    }
}

// ── Main Panel ─────────────────────────────────────────────────

interface WorkspaceSettingsProps {
    isOpen: boolean
    onClose: () => void
    onOpenPicker: () => void
    onSelectFile?: (path: string) => void
    onOpenNotificationDevices?: () => void
}

export default function WorkspaceSettings({
    isOpen,
    onClose,
    onOpenPicker,
    onSelectFile,
    onOpenNotificationDevices,
}: WorkspaceSettingsProps) {
    const ws = useWorkspace()
    const { blobId, canWrite, label, emoji } = useWorkspaceMetadata()
    const { entries, updateEntry } = useRegistry()
    const { online, syncing, syncPaused, syncPolicy: runtimeSyncPolicy, setSyncPolicy } = useSync()
    const { connections: mcpConnections, readLiveConfig } = useMcpConnections()
    const toast = useToast()

    const [writers, setWriters] = useState<WriterInfo[]>([])
    const [viewers, setViewers] = useState<PresenceEntry[]>([])
    const [editMode, setEditMode] = useState(false)
    const [showDelete, setShowDelete] = useState(false)
    const [showLeave, setShowLeave] = useState(false)
    const [removing, setRemoving] = useState<string | null>(null)
    const [pendingRemoveKid, setPendingRemoveKid] = useState<string | null>(null)
    const [notifState, setNotifState] = useState<NotificationState | null>(null)
    const [notificationConfig, setNotificationConfig] = useState<NotificationConfigDocument | null>(null)
    const [notifBusy, setNotifBusy] = useState(false)
    const [liveMcpConfigs, setLiveMcpConfigs] = useState<Record<string, McpConfig>>({})
    const [mcpLinkStatus, setMcpLinkStatus] = useState<'idle' | 'loading' | 'loaded'>('idle')
    const [mcpLiveErrorCount, setMcpLiveErrorCount] = useState(0)
    const [mcpCredentialKids, setMcpCredentialKids] = useState<Record<string, WorkspaceMcpCredentialKids>>({})
    const [copiedMcpField, setCopiedMcpField] = useState<string | null>(null)
    const registryEntry = entries.find(entry => entry.blobId === blobId)
    const notificationApiBaseUrl = registryEntry?.apiBaseUrl ?? API_BASE_URL
    const notificationsEnabled = registryEntry?.notifications?.push === true
    const [workspaceSyncMode, setWorkspaceSyncMode] = useState<WorkspaceSyncPolicy>(() => getLocalWorkspaceSyncPolicy(blobId))
    const workspacePausedSync = workspaceSyncMode === 'paused'
    const [workspaceSyncBusy, setWorkspaceSyncBusy] = useState(false)
    const workspaceMcpRefs = useMemo(
        () => new Set([blobId, label, registryEntry?.label].filter((value): value is string => Boolean(value))),
        [blobId, label, registryEntry?.label],
    )
    const workspaceMcpLinks = useMemo(
        () => mcpConnections
            .map(connection => resolveWorkspaceMcpLink(connection, liveMcpConfigs[connection.configBlobId], workspaceMcpRefs, blobId))
            .filter((link): link is WorkspaceMcpLink => link !== null),
        [blobId, liveMcpConfigs, mcpConnections, workspaceMcpRefs],
    )

    // Load writers and presence
    useEffect(() => {
        if (!isOpen) return
        setWriters(ws.getWriters())
        setViewers(ws.presence.getViewers())
        const unsub = ws.presence.on('presence:changed', setViewers)
        return unsub
    }, [isOpen, ws])

    useEffect(() => {
        setWorkspaceSyncMode(getLocalWorkspaceSyncPolicy(blobId))
        setWorkspaceSyncBusy(false)
    }, [blobId, isOpen])

    // Escape to close — skip when a sub-modal is open so Escape only closes the modal.
    useEffect(() => {
        if (!isOpen) return
        const handleKey = (e: KeyboardEvent) => {
            if (e.key !== 'Escape') return
            if (showDelete || showLeave || pendingRemoveKid) return
            onClose()
        }
        window.addEventListener('keydown', handleKey)
        return () => window.removeEventListener('keydown', handleKey)
    }, [isOpen, onClose, pendingRemoveKid, showDelete, showLeave])

    // Load notification state when panel opens (spec §19.2)
    useEffect(() => {
        if (!isOpen) {
            setNotifState(null)
            setNotificationConfig(null)
            return
        }
        getNotificationState(notificationApiBaseUrl, notificationsEnabled).then(setNotifState).catch(() => setNotifState(null))
    }, [isOpen, notificationApiBaseUrl, notificationsEnabled])

    useEffect(() => {
        if (!isOpen) {
            setNotificationConfig(null)
            return
        }

        let cancelled = false
        ws.fs.readText(NOTIFICATION_CONFIG_PATH)
            .then(text => {
                if (cancelled) return
                setNotificationConfig(parseNotificationConfig(text, blobId))
            })
            .catch(() => {
                if (cancelled) return
                setNotificationConfig(null)
            })

        return () => {
            cancelled = true
        }
    }, [blobId, isOpen, ws])

    const refreshMcpLiveLinks = useCallback(async () => {
        if (mcpConnections.length === 0) {
            setLiveMcpConfigs({})
            setMcpLiveErrorCount(0)
            setMcpLinkStatus('loaded')
            return
        }

        setMcpLinkStatus('loading')
        const results = await Promise.allSettled(
            mcpConnections.map(async connection => [
                connection.configBlobId,
                await readLiveConfig(connection),
            ] as const),
        )

        const nextConfigs: Record<string, McpConfig> = {}
        let errorCount = 0
        for (const result of results) {
            if (result.status === 'fulfilled') {
                const [configBlobId, config] = result.value
                nextConfigs[configBlobId] = config
            } else {
                errorCount += 1
            }
        }

        setLiveMcpConfigs(nextConfigs)
        setMcpLiveErrorCount(errorCount)
        setMcpLinkStatus('loaded')
    }, [mcpConnections, readLiveConfig])

    useEffect(() => {
        setLiveMcpConfigs({})
        setMcpCredentialKids({})
        setMcpLinkStatus('idle')
        setMcpLiveErrorCount(0)
    }, [blobId, isOpen])

    useEffect(() => {
        if (!isOpen) return
        if (mcpLinkStatus !== 'idle') return
        void refreshMcpLiveLinks()
    }, [isOpen, mcpLinkStatus, refreshMcpLiveLinks])

    useEffect(() => {
        let cancelled = false
        void Promise.all(workspaceMcpLinks.map(async link => {
            const reader = link.readerSeedForKid
                ? await deriveMcpSeedIdentity(link.readerSeedForKid).catch(() => null)
                : null
            const writer = link.writerSeedForKid
                ? await deriveMcpSeedIdentity(link.writerSeedForKid).catch(() => null)
                : null
            return [
                link.configBlobId,
                {
                    ...(reader ? { readerKid: reader.kid } : {}),
                    ...(writer ? { writerKid: writer.kid } : {}),
                },
            ] as const
        })).then(entries => {
            if (!cancelled) setMcpCredentialKids(Object.fromEntries(entries))
        })
        return () => {
            cancelled = true
        }
    }, [workspaceMcpLinks])

    const copyMcpField = useCallback((value: string, field: string) => {
        void navigator.clipboard?.writeText(value).catch(() => undefined)
        setCopiedMcpField(field)
        window.setTimeout(() => setCopiedMcpField(null), 1600)
    }, [])

    const getNotificationAuthProvider = useCallback(() => {
        return registryEntry
            ? createWorkspaceAuthProvider(notificationApiBaseUrl, registryEntry.capability, blobId)
            : null
    }, [blobId, notificationApiBaseUrl, registryEntry])

    const syncNotificationConfig = useCallback(async (
        authProvider: NonNullable<ReturnType<typeof createWorkspaceAuthProvider>>,
        opts: { rotate?: boolean } = {},
    ) => {
        const nextConfig = await provisionNotificationConfig(notificationApiBaseUrl, blobId, authProvider, opts)
        const existingConfig = parseNotificationConfig(await ws.fs.readText(NOTIFICATION_CONFIG_PATH), blobId)
        const changed = opts.rotate === true || !isSameNotificationConfig(existingConfig, nextConfig)
        if (changed) {
            ws.fs.writeText(NOTIFICATION_CONFIG_PATH, formatNotificationConfig(nextConfig))
        }
        setNotificationConfig(nextConfig)
        return { config: nextConfig, changed }
    }, [blobId, notificationApiBaseUrl, ws])

    const handleRemoveWriter = useCallback(async (kid: string) => {
        setRemoving(kid)
        setPendingRemoveKid(null)
        const name = writers.find(w => w.kid === kid)?.name || truncateKid(kid)
        try {
            await ws.removeWriter(kid)
            setWriters(ws.getWriters())
            toast(`Removed ${name}`, { type: 'success' })
        } catch (err) {
            toast(`Failed to remove: ${err instanceof Error ? err.message : 'Unknown error'}`, { type: 'error' })
        } finally {
            setRemoving(null)
        }
    }, [toast, ws, writers])

    const handleToggleNotifications = useCallback(async () => {
        if (notifState === null || notifBusy) return
        setNotifBusy(true)
        try {
            if (notifState === 'on') {
                const otherEnabledEntries = entries.filter(entry => entry.blobId !== blobId && entry.notifications?.push)
                if (otherEnabledEntries.length === 0) {
                    const removed = await unsubscribeFromPush(notificationApiBaseUrl)
                    if (!removed) {
                        toast('Failed to disable notifications', { type: 'error' })
                        return
                    }
                    await unsubscribeBrowserPush()
                } else {
                    const authProvider = registryEntry
                        ? createWorkspaceAuthProvider(notificationApiBaseUrl, registryEntry.capability, blobId)
                        : null
                    if (!authProvider) {
                        toast('Cannot disable notifications: no workspace auth available', { type: 'error' })
                        return
                    }

                    const removed = await unsubscribeFromPush(notificationApiBaseUrl, [{
                        blobId,
                        authProvider,
                    }])
                    if (!removed) {
                        toast('Failed to disable notifications', { type: 'error' })
                        return
                    }
                }

                updateEntry(blobId, {
                    notifications: {
                        ...registryEntry?.notifications,
                        push: false,
                    },
                })
                const newState = await getNotificationState(notificationApiBaseUrl, false)
                setNotifState(newState)
                toast('Notifications disabled for this device', { type: 'info' })
            } else if (notifState === 'off') {
                const authProvider = getNotificationAuthProvider()
                if (!authProvider) {
                    toast('Cannot enable notifications: no workspace auth available', { type: 'error' })
                    return
                }
                const otherEnabledEntries = entries.filter(entry => entry.blobId !== blobId && entry.notifications?.push)
                const success = await subscribeToPush(notificationApiBaseUrl, [{
                    blobId,
                    authProvider,
                }])
                if (success) {
                    try {
                        const { changed } = await syncNotificationConfig(authProvider)
                        updateEntry(blobId, {
                            notifications: {
                                ...registryEntry?.notifications,
                                push: true,
                            },
                        })
                        const newState = await getNotificationState(notificationApiBaseUrl, true)
                        setNotifState(newState)
                        toast(
                            changed
                                ? 'Notifications enabled and notification config synced'
                                : 'Notifications enabled for this workspace on this device',
                            { type: 'success' },
                        )
                    } catch (err) {
                        await unsubscribeFromPush(notificationApiBaseUrl, [{
                            blobId,
                            authProvider,
                        }]).catch(() => false)
                        if (otherEnabledEntries.length === 0) {
                            await unsubscribeBrowserPush().catch(() => undefined)
                        }
                        throw err
                    }
                } else {
                    toast('Failed to enable notifications', { type: 'error' })
                }
            } else {
                const messages: Record<string, string> = {
                    'requires-install': 'Install this app as a PWA to enable push notifications',
                    'permission-blocked': 'Notifications are blocked in your browser settings. Please enable them manually.',
                    'not-supported': 'Push notifications are not supported in this browser',
                    'server-unavailable': 'Notification server is not available. Try again later.',
                }
                toast(messages[notifState] ?? 'Cannot enable notifications in current state', { type: 'info' })
            }
        } catch (err) {
            toast(`Notification error: ${err instanceof Error ? err.message : 'Unknown error'}`, { type: 'error' })
        } finally {
            setNotifBusy(false)
        }
    }, [blobId, entries, getNotificationAuthProvider, notifBusy, notifState, notificationApiBaseUrl, registryEntry, syncNotificationConfig, toast, updateEntry])

    const handleRotateNotificationCredential = useCallback(async () => {
        if (notifBusy) return
        const authProvider = getNotificationAuthProvider()
        if (!authProvider) {
            toast('Cannot rotate notification credential: no workspace auth available', { type: 'error' })
            return
        }

        setNotifBusy(true)
        try {
            await syncNotificationConfig(authProvider, { rotate: true })
            toast('Notification credential rotated', { type: 'success' })
        } catch (err) {
            toast(`Notification error: ${err instanceof Error ? err.message : 'Unknown error'}`, { type: 'error' })
        } finally {
            setNotifBusy(false)
        }
    }, [getNotificationAuthProvider, notifBusy, syncNotificationConfig, toast])

    const handleOpenAutomationFile = useCallback((path: string) => {
        onSelectFile?.(path)
        onClose()
    }, [onClose, onSelectFile])

    const handleWorkspaceSyncPolicyChange = useCallback((nextMode: WorkspaceSyncPolicy) => {
        if (workspaceSyncBusy) return
        setLocalWorkspaceSyncPolicy(blobId, nextMode)
        setWorkspaceSyncMode(nextMode)
        setSyncPolicy(nextMode)
        if (nextMode === 'paused') {
            toast('Sync paused for this workspace on this device. Local edits stay queued until sync resumes.', { type: 'info' })
        } else if (nextMode === 'auto') {
            toast('Auto-sync enabled. SecureBlob will try to make workspace content available offline.', { type: 'success' })
        } else {
            toast('On-demand sync enabled. File tree stays current; content downloads when opened.', { type: 'success' })
        }
    }, [blobId, setSyncPolicy, toast, workspaceSyncBusy])

    const handleOfflineSyncNow = useCallback(async () => {
        if (!workspacePausedSync || workspaceSyncBusy) return
        setWorkspaceSyncBusy(true)
        try {
            ws.setSyncPolicy('on-demand')
            await ws.syncNow()
            const latestMode = getLocalWorkspaceSyncPolicy(blobId)
            if (latestMode === 'paused') {
                ws.setSyncPolicy('paused')
                toast('Manual sync completed. Workspace remains paused.', { type: 'success' })
            } else {
                ws.setSyncPolicy(latestMode)
                toast('Manual sync completed.', { type: 'success' })
            }
        } catch (err) {
            if (workspacePausedSync) ws.setSyncPolicy('paused')
            toast(`Manual sync failed: ${err instanceof Error ? err.message : 'Unknown error'}`, { type: 'error' })
        } finally {
            setWorkspaceSyncBusy(false)
        }
    }, [blobId, toast, workspacePausedSync, workspaceSyncBusy, ws])

    if (!isOpen) return null

    const ownerKid = writers[0]?.kid
    const selfEntry = viewers.find(v => v.isSelf)
    const selfKid = selfEntry?.kid

    return (
        <>
            <div className="ws-settings-backdrop" onClick={onClose} />
            <div className="ws-settings-panel" role="dialog" aria-label="Workspace settings">

                <div className="ws-settings-header">
                    <h2><Settings size={16} /> Workspace Settings</h2>
                    <button className="btn btn-icon" onClick={onClose} aria-label="Close"><X size={18} /></button>
                </div>

                <div className="ws-settings-identity">
                    <span className="ws-settings-emoji">{emoji}</span>
                    <div className="ws-settings-name">
                        <h3>{label}</h3>
                        <div className="ws-settings-blobid">{blobId}</div>
                    </div>
                </div>

                {(mcpConnections.length > 0 || workspaceMcpLinks.length > 0) && (
                    <div className="ws-settings-section">
                        <div className="ws-settings-section-header">
                            <span className="ws-settings-section-title">
                                <GitBranch size={13} /> MCP Access
                                <span className="ws-settings-section-badge">{workspaceMcpLinks.length}</span>
                            </span>
                            <button
                                className="ws-settings-section-action"
                                onClick={() => void refreshMcpLiveLinks()}
                                disabled={mcpLinkStatus === 'loading'}
                                type="button"
                            >
                                <RefreshCw size={12} /> Refresh
                            </button>
                        </div>

                        {workspaceMcpLinks.length === 0 ? (
                            <div className="ws-settings-empty">
                                {mcpLinkStatus === 'loading'
                                    ? 'Checking live MCP configs…'
                                    : 'No MCP configs reference this workspace in the local registry or readable live configs.'}
                            </div>
                        ) : (
                            <div className="ws-settings-mcp-list">
                                {workspaceMcpLinks.map(link => {
                                    const kids = mcpCredentialKids[link.configBlobId]
                                    return (
                                        <div key={link.configBlobId} className="ws-settings-mcp-link">
                                            <div className="ws-settings-mcp-main">
                                                <GitBranch size={14} />
                                                <div className="ws-settings-mcp-copy">
                                                    <strong>{link.label}</strong>
                                                    <button
                                                        type="button"
                                                        className="ws-settings-mcp-code"
                                                        onClick={() => copyMcpField(link.configBlobId, `config:${link.configBlobId}`)}
                                                        title="Copy config blob id"
                                                    >
                                                        {copiedMcpField === `config:${link.configBlobId}` ? 'Copied' : link.configBlobId}
                                                    </button>
                                                </div>
                                            </div>
                                            <div className="ws-settings-mcp-badges">
                                                {link.purpose && <span className="ws-settings-mcp-badge">{link.purpose}</span>}
                                                <span className="ws-settings-mcp-badge">{link.access}</span>
                                                <span className="ws-settings-mcp-badge muted">{formatMcpLinkSource(link.source)}</span>
                                                <span className={`ws-settings-mcp-badge ${link.hasRegistryReader ? '' : 'muted'}`}>registry reader</span>
                                                <span className={`ws-settings-mcp-badge ${link.hasRegistryWriter ? '' : 'muted'}`}>registry writer</span>
                                                {kids?.readerKid && (
                                                    <button
                                                        type="button"
                                                        className="ws-settings-mcp-kid"
                                                        onClick={() => copyMcpField(kids.readerKid!, `reader:${link.configBlobId}`)}
                                                        title="Copy reader kid"
                                                    >
                                                        <KeyRound size={11} />
                                                        reader {copiedMcpField === `reader:${link.configBlobId}` ? 'Copied' : shortAccessKid(kids.readerKid)}
                                                    </button>
                                                )}
                                                {kids?.writerKid && (
                                                    <button
                                                        type="button"
                                                        className="ws-settings-mcp-kid"
                                                        onClick={() => copyMcpField(kids.writerKid!, `writer:${link.configBlobId}`)}
                                                        title="Copy writer kid"
                                                    >
                                                        <KeyRound size={11} />
                                                        writer {copiedMcpField === `writer:${link.configBlobId}` ? 'Copied' : shortAccessKid(kids.writerKid)}
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    )
                                })}
                            </div>
                        )}

                        {mcpLiveErrorCount > 0 && (
                            <div className="ws-settings-mcp-hint">
                                {mcpLiveErrorCount} MCP config{mcpLiveErrorCount === 1 ? '' : 's'} could not be read live.
                            </div>
                        )}
                    </div>
                )}

                <div className="ws-settings-section">
                    <div className="ws-settings-section-header">
                        <span className="ws-settings-section-title">
                            <Users size={13} /> Members
                            <span className="ws-settings-section-badge">{writers.length}</span>
                        </span>
                        {canWrite && writers.length > 1 && (
                            <button
                                className="ws-settings-section-action"
                                onClick={() => setEditMode(!editMode)}
                            >
                                {editMode ? 'Done' : '✏ Edit'}
                            </button>
                        )}
                    </div>

                    {writers.length === 0 ? (
                        <div className="ws-settings-empty">No member data available</div>
                    ) : (
                        writers.map(w => {
                            const isOnline = viewers.some(v => v.kid === w.kid)
                            const isSelf = w.kid === selfKid
                            const isOwner = w.kid === ownerKid
                            const role = isOwner ? 'owner' : 'editor'

                            return (
                                <div key={w.kid} className="ws-settings-member">
                                    <span className="ws-settings-member-emoji">
                                        {w.emoji || '👤'}
                                    </span>
                                    <div className="ws-settings-member-info">
                                        <div className="ws-settings-member-name">
                                            {w.name || truncateKid(w.kid)}
                                            {isSelf && <span className="ws-settings-you-badge">(you)</span>}
                                        </div>
                                        <div className="ws-settings-member-role">{role}</div>
                                    </div>
                                    {isOnline && <span className="ws-settings-member-presence-dot" />}
                                    {editMode && !isSelf && !isOwner && (
                                        <button
                                            className="ws-settings-remove-btn"
                                            onClick={() => setPendingRemoveKid(w.kid)}
                                            disabled={removing === w.kid}
                                        >
                                            {removing === w.kid
                                                ? <span className="ws-settings-spinner" />
                                                : '× Remove'}
                                        </button>
                                    )}
                                </div>
                            )
                        })
                    )}
                </div>

                {notifState !== null && (
                    <div className="ws-settings-section">
                        <div className="ws-settings-section-header">
                            <span className="ws-settings-section-title">
                                <Bell size={13} /> Notifications
                            </span>
                            <button
                                className={`ws-settings-notif-toggle ${notifState === 'on' ? 'active' : ''}`}
                                onClick={handleToggleNotifications}
                                disabled={notifBusy || notifState === 'permission-blocked'}
                                title={
                                    notifState === 'on' ? 'Disable notifications on this device'
                                        : notifState === 'off' ? 'Enable notifications on this device'
                                            : notifState === 'requires-install' ? 'Install the app to enable notifications'
                                                : notifState === 'permission-blocked' ? 'Notifications blocked by browser'
                                                    : 'Notifications unavailable'
                                }
                            >
                                {notifBusy ? <span className="ws-settings-spinner" />
                                    : notifState === 'on' ? <><Bell size={13} /> On</>
                                        : <><BellOff size={13} /> Off</>
                                }
                            </button>
                        </div>
                        {notifState === 'requires-install' && (
                            <div className="ws-settings-notif-hint">
                                Install this app as a PWA to enable push notifications on this device.
                            </div>
                        )}
                        {notifState === 'permission-blocked' && (
                            <div className="ws-settings-notif-hint warning">
                                Notifications are blocked in your browser settings. Open browser settings to allow them.
                            </div>
                        )}
                        {notifState === 'not-supported' && (
                            <div className="ws-settings-notif-hint warning">
                                Push notifications are not supported on this browser, but you can still manage registered devices.
                            </div>
                        )}
                        {notifState === 'server-unavailable' && (
                            <div className="ws-settings-notif-hint warning">
                                Notification server status is unavailable right now, but you can still open device management.
                            </div>
                        )}
                        {(notificationConfig || notifState === 'on') && (
                            <div className="ws-settings-notif-actions">
                                <button
                                    className="ws-settings-secondary-btn"
                                    onClick={() => handleOpenAutomationFile(NOTIFICATION_CONFIG_PATH)}
                                    disabled={!notificationConfig}
                                >
                                    Open notification config
                                </button>
                                {canWrite && (
                                    <button
                                        className="ws-settings-secondary-btn"
                                        onClick={handleRotateNotificationCredential}
                                        disabled={notifBusy}
                                    >
                                        Rotate notification credential
                                    </button>
                                )}
                            </div>
                        )}
                        {onOpenNotificationDevices && (
                            <button
                                className="ws-settings-notif-manage"
                                onClick={onOpenNotificationDevices}
                                type="button"
                            >
                                Manage devices…
                            </button>
                        )}
                    </div>
                )}

                <div className="ws-settings-section">
                    <div className="ws-settings-section-header ws-settings-section-header-stacked">
                        <span className="ws-settings-section-title">
                            <RefreshCw size={13} /> Sync Behavior
                        </span>
                    </div>
                    <div className="ws-settings-sync-mode-group" role="group" aria-label="Workspace sync policy">
                        {WORKSPACE_SYNC_POLICY_OPTIONS.map(option => (
                            <button
                                key={option.policy}
                                className={`ws-settings-sync-mode-button ${workspaceSyncMode === option.policy ? 'active' : ''}`}
                                onClick={() => handleWorkspaceSyncPolicyChange(option.policy)}
                                title={option.title}
                                aria-label={option.label}
                                aria-pressed={workspaceSyncMode === option.policy}
                                disabled={workspaceSyncBusy}
                                type="button"
                            >
                                <span className="ws-settings-sync-mode-label">
                                    {option.label}
                                    {option.badge && <span className="ws-settings-sync-mode-badge">{option.badge}</span>}
                                </span>
                                <span className="ws-settings-sync-mode-caption">{option.caption}</span>
                            </button>
                        ))}
                    </div>
                    <div className="ws-settings-sync-hint">
                        {workspaceSyncMode === 'paused'
                            ? 'Background sync is paused for this workspace on this device. Local edits are queued until sync resumes.'
                            : workspaceSyncMode === 'auto'
                                ? 'File tree, file content, and known SQLite lanes sync proactively for offline use.'
                                : syncPaused || runtimeSyncPolicy === 'paused'
                                    ? 'Background sync is paused. Choose a sync policy here or resume from the sync popover.'
                                    : 'File tree stays current. File content and SQLite lanes sync when opened; edits still upload when online.'}
                    </div>
                    {workspacePausedSync && (
                        <div className="ws-settings-notif-actions">
                            <button
                                className="ws-settings-secondary-btn"
                                onClick={() => void handleOfflineSyncNow()}
                                disabled={syncing || workspaceSyncBusy}
                                type="button"
                            >
                                {syncing || workspaceSyncBusy ? 'Syncing…' : 'Sync now once'}
                            </button>
                            <span className="ws-settings-sync-pending">
                                Pending changes: {ws.state.pendingChanges}
                            </span>
                        </div>
                    )}
                </div>

                <div className="ws-settings-section">
                    <div className="ws-settings-section-header">
                        <span className="ws-settings-section-title">
                            <Eye size={13} /> Viewing now
                            <span className="ws-settings-section-badge">{viewers.length}</span>
                        </span>
                    </div>
                    {viewers.length === 0 ? (
                        <div className="ws-settings-empty">No one else is viewing</div>
                    ) : (
                        viewers.map((v, i) => (
                            <div key={v.kid + i} className="ws-settings-viewer">
                                <span className="ws-settings-viewer-emoji">{v.emoji || '👤'}</span>
                                <span className="ws-settings-viewer-name">
                                    {v.name || truncateKid(v.kid)}
                                    {v.isSelf && <span className="ws-settings-you-badge"> (you)</span>}
                                </span>
                                {v.device && v.device !== 'web' && (
                                    <span className="ws-settings-viewer-device">{v.device}</span>
                                )}
                                {v.activeFile && (
                                    <span className="ws-settings-viewer-file">{v.activeFile}</span>
                                )}
                            </div>
                        ))
                    )}
                </div>

                <ServerJobsPanel
                    onOpenFile={handleOpenAutomationFile}
                    canWrite={canWrite}
                    workspaceLabel={label}
                />

                <div className="ws-settings-danger">
                    <div className="ws-settings-danger-title">
                        <AlertTriangle size={14} /> Danger Zone
                    </div>
                    <div className="ws-settings-danger-actions">
                        <button
                            className="ws-settings-danger-btn leave"
                            onClick={() => setShowLeave(true)}
                        >
                            <LogOut size={15} /> Leave workspace
                        </button>
                        {canWrite && (
                            <button
                                className="ws-settings-danger-btn delete"
                                onClick={() => setShowDelete(true)}
                                disabled={!online}
                                title={!online ? 'Must be online to delete' : undefined}
                            >
                                <Trash2 size={15} /> Delete workspace
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {showDelete && (
                <DeleteWorkspaceModal
                    workspace={ws}
                    label={label}
                    emoji={emoji}
                    writers={writers}
                    onClose={() => setShowDelete(false)}
                    onDeleted={() => {
                        setShowDelete(false)
                        onClose()
                        onOpenPicker()
                    }}
                />
            )}

            {showLeave && (
                <LeaveWorkspaceModal
                    blobId={blobId}
                    label={label}
                    emoji={emoji}
                    onClose={() => setShowLeave(false)}
                    onLeft={() => {
                        setShowLeave(false)
                        onClose()
                        onOpenPicker()
                    }}
                />
            )}

            {pendingRemoveKid && (() => {
                const writer = writers.find(w => w.kid === pendingRemoveKid)
                const name = writer?.name || truncateKid(pendingRemoveKid)
                const emojiStr = writer?.emoji || '👤'
                return (
                    <RemoveWriterModal
                        name={name}
                        emoji={emojiStr}
                        onConfirm={() => handleRemoveWriter(pendingRemoveKid)}
                        onCancel={() => setPendingRemoveKid(null)}
                    />
                )
            })()}
        </>
    )
}

// ── Delete Workspace Modal (type-to-confirm) ───────────────────

interface DeleteWorkspaceModalProps {
    workspace: Workspace
    label: string
    emoji: string
    writers: WriterInfo[]
    onClose: () => void
    onDeleted: () => void
}

function DeleteWorkspaceModal({ workspace, label, writers, onClose, onDeleted }: DeleteWorkspaceModalProps) {
    const { removeEntry } = useRegistry()
    const toast = useToast()
    const [confirmText, setConfirmText] = useState('')
    const [deleting, setDeleting] = useState(false)
    const inputRef = useRef<HTMLInputElement>(null)

    const canSubmit = confirmText === 'DELETE' && !deleting

    useEffect(() => {
        inputRef.current?.focus()
    }, [])

    useEffect(() => {
        const handleKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose()
        }
        window.addEventListener('keydown', handleKey)
        return () => window.removeEventListener('keydown', handleKey)
    }, [onClose])

    const handleDelete = async () => {
        if (!canSubmit) return
        setDeleting(true)
        try {
            await workspace.commitTombstone()
            removeEntry(workspace.blobId)
            toast(`"${label}" deleted permanently`, { type: 'success' })
            onDeleted()
        } catch (err) {
            toast(`Deletion failed: ${err instanceof Error ? err.message : 'Unknown error'}`, { type: 'error' })
            setDeleting(false)
        }
    }

    return (
        <>
            <div className="ws-delete-backdrop" onClick={onClose} />
            <div className="ws-delete-modal" role="dialog" aria-label="Delete workspace">
                <div className="ws-delete-header">
                    <span className="ws-delete-icon">🗑</span>
                    <h3>Delete &quot;{label}&quot;?</h3>
                </div>

                <div className="ws-delete-body">
                    <div className="ws-delete-warning">
                        <AlertTriangle size={16} />
                        <div>
                            <strong>Permanent and irreversible.</strong><br />
                            All members lose access. Historical data cannot be recovered.
                        </div>
                    </div>

                    <div className="ws-delete-affected">
                        <span>
                            <Users size={14} />
                            {writers.length} affected member{writers.length !== 1 ? 's' : ''}:
                            {' '}{writers.map(w => w.emoji || '👤').join(' ')}
                        </span>
                    </div>

                    <div className="ws-delete-confirm-label">
                        Type <strong>&quot;DELETE&quot;</strong> to confirm:
                    </div>
                    <input
                        ref={inputRef}
                        className="ws-delete-confirm-input"
                        type="text"
                        value={confirmText}
                        onChange={e => setConfirmText(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter' && canSubmit) handleDelete() }}
                        placeholder="DELETE"
                        autoComplete="off"
                        spellCheck={false}
                    />
                </div>

                <div className="ws-delete-footer">
                    <button className="ws-delete-cancel" onClick={onClose}>Cancel</button>
                    <button
                        className="ws-delete-submit"
                        disabled={!canSubmit}
                        onClick={handleDelete}
                    >
                        {deleting ? <><span className="ws-settings-spinner" /> Deleting…</> : 'Delete forever'}
                    </button>
                </div>
            </div>
        </>
    )
}

// ── Leave Workspace Modal ──────────────────────────────────────

interface LeaveWorkspaceModalProps {
    blobId: string
    label: string
    emoji: string
    onClose: () => void
    onLeft: () => void
}

function LeaveWorkspaceModal({ blobId, label, onClose, onLeft }: LeaveWorkspaceModalProps) {
    const manager = useEngineManager()
    const toast = useToast()

    useEffect(() => {
        const handleKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose()
        }
        window.addEventListener('keydown', handleKey)
        return () => window.removeEventListener('keydown', handleKey)
    }, [onClose])

    const handleLeave = () => {
        manager.leaveWorkspace(blobId)
        toast(`Left "${label}"`, { type: 'info' })
        onLeft()
    }

    return (
        <>
            <div className="ws-delete-backdrop" onClick={onClose} />
            <div className="ws-leave-modal" role="dialog" aria-label="Leave workspace">
                <div className="ws-leave-body">
                    <h3><LogOut size={18} /> Leave &quot;{label}&quot;?</h3>
                    <p>Removed from your list only. Others keep access.</p>
                    <p>You can rejoin via a share link.</p>
                </div>
                <div className="ws-leave-footer">
                    <button className="ws-leave-cancel" onClick={onClose}>Cancel</button>
                    <button className="ws-leave-submit" onClick={handleLeave}>
                        <LogOut size={14} /> Leave
                    </button>
                </div>
            </div>
        </>
    )
}

// ── Remove Writer Modal (in-app confirmation) ──────────────────

interface RemoveWriterModalProps {
    name: string
    emoji: string
    onConfirm: () => void
    onCancel: () => void
}

function RemoveWriterModal({ name, emoji, onConfirm, onCancel }: RemoveWriterModalProps) {
    useEffect(() => {
        const handleKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onCancel()
        }
        window.addEventListener('keydown', handleKey)
        return () => window.removeEventListener('keydown', handleKey)
    }, [onCancel])

    return (
        <>
            <div className="ws-delete-backdrop" onClick={onCancel} />
            <div className="ws-leave-modal" role="dialog" aria-label={`Remove ${name}`}>
                <div className="ws-leave-body">
                    <h3>Remove {emoji} {name}?</h3>
                    <p>They will lose write access and cannot decrypt future content.</p>
                </div>
                <div className="ws-leave-footer">
                    <button className="ws-leave-cancel" onClick={onCancel}>Cancel</button>
                    <button className="ws-remove-writer-confirm" onClick={onConfirm}>
                        Remove
                    </button>
                </div>
            </div>
        </>
    )
}

// ── Workspace Deleted Overlay ──────────────────────────────────

interface WorkspaceDeletedOverlayProps {
    label: string
    deletedByName?: string
    onOpenPicker: () => void
}

export function WorkspaceDeletedOverlay({ label, deletedByName, onOpenPicker }: WorkspaceDeletedOverlayProps) {
    return (
        <div className="ws-deleted-overlay">
            <div className="ws-deleted-icon">📦</div>
            <h2 className="ws-deleted-title">Workspace Deleted</h2>
            <p className="ws-deleted-subtitle">
                &quot;{label}&quot; was deleted{deletedByName ? ` by ${deletedByName}` : ''}.
            </p>
            <button className="ws-deleted-action" onClick={onOpenPicker}>
                Open another workspace
            </button>
        </div>
    )
}

// ── Presence Indicator (TopBar) ────────────────────────────────

interface PresenceIndicatorProps {
    onClick?: () => void
}

export function PresenceIndicator({ onClick }: PresenceIndicatorProps) {
    const ws = useWorkspace()
    const [viewers, setViewers] = useState<PresenceEntry[]>([])

    useEffect(() => {
        setViewers(ws.presence.getViewers())
        const unsub = ws.presence.on('presence:changed', setViewers)
        return unsub
    }, [ws])

    if (viewers.length === 0) return null

    const self = viewers.find(v => v.isSelf)
    const others = viewers.filter(v => !v.isSelf)
    const allToShow = [self, ...others].filter(Boolean) as PresenceEntry[]
    const maxAvatars = 4
    const overflow = allToShow.length - maxAvatars

    const tooltipLines = allToShow.map(v =>
        `${v.emoji || '👤'} ${v.isSelf ? 'You' : (v.name || v.kid.slice(0, 8))}${v.activeFile ? ` — ${v.activeFile}` : ''}`,
    )
    const tooltip = tooltipLines.join('\n')

    return (
        <button
            className="presence-chip"
            onClick={onClick}
            title={tooltip}
            aria-label={`${allToShow.length} ${allToShow.length === 1 ? 'person' : 'people'} here`}
        >
            <span className="presence-chip-stack">
                {allToShow.slice(0, maxAvatars).map((v, i) => (
                    <span
                        key={v.kid}
                        className={`presence-chip-avatar ${v.isSelf ? 'presence-chip-self' : ''}`}
                        style={{ zIndex: maxAvatars - i }}
                    >
                        {v.emoji || '👤'}
                    </span>
                ))}
                {overflow > 0 && (
                    <span className="presence-chip-avatar presence-chip-overflow" style={{ zIndex: 0 }}>
                        +{overflow}
                    </span>
                )}
            </span>
            <span className="presence-chip-label">
                {others.length === 0
                    ? 'You'
                    : others.length === 1
                        ? `${others[0].name || others[0].kid.slice(0, 8)}`
                        : `${allToShow.length} here`
                }
            </span>
            <span className="presence-chip-dot" />
        </button>
    )
}
